import { useParams } from "react-router-dom";
import { usePublicShare } from "@/resources/share";
import { PublicShareLayout } from "@/components/share-views/PublicShareLayout";
import { PublicScorecardView } from "@/components/share-views/PublicScorecardView";
import { PublicLandscapeView } from "@/components/share-views/PublicLandscapeView";
import { PublicRankTrackerView } from "@/components/share-views/PublicRankTrackerView";
import { PublicRankTrackerPageView } from "@/components/share-views/PublicRankTrackerPageView";
import { PublicSeoInsightsView } from "@/components/share-views/PublicSeoInsightsView";
import { PublicSearchTermSetView } from "@/components/share-views/PublicSearchTermSetView";
import type { SPIResult, LandscapeOutputData, RankTrackerReport, PublicRankTrackerPagePayload } from "@/types";
import type { SearchTermSet } from "@/types/search-term";
import type { PublicSeoInsightsPayload } from "@/components/share-views/PublicSeoInsightsView";

function LoadingSkeleton() {
  return (
    <div className="space-y-4 animate-pulse">
      <div className="h-8 w-64 bg-muted rounded" />
      <div className="h-4 w-48 bg-muted rounded" />
      <div className="h-48 w-full bg-muted rounded-lg" />
      <div className="grid grid-cols-3 gap-4">
        <div className="h-32 bg-muted rounded-lg" />
        <div className="h-32 bg-muted rounded-lg" />
        <div className="h-32 bg-muted rounded-lg" />
      </div>
    </div>
  );
}

function NotFoundView() {
  return (
    <div className="flex flex-col items-center justify-center min-h-[50vh] text-center">
      <h1 className="text-2xl font-semibold text-foreground mb-2">
        This link is no longer available
      </h1>
      <p className="text-muted-foreground">
        The share link may have expired or been revoked.
      </p>
    </div>
  );
}

export default function PublicShareView() {
  const { token } = useParams<{ token: string }>();
  const { data, isLoading, isError } = usePublicShare(token);

  return (
    <>
      <meta name="robots" content="noindex,nofollow" />
      {isLoading && (
        <div className="min-h-screen bg-white">
          <header className="border-b border-border bg-white px-6 py-3 flex items-center justify-between">
            <div className="h-4 w-32 bg-muted rounded animate-pulse" />
          </header>
          <main className="px-6 py-8">
            <LoadingSkeleton />
          </main>
        </div>
      )}
      {!isLoading && (isError || !data) && (
        <div className="min-h-screen bg-white flex flex-col items-center justify-center">
          <NotFoundView />
        </div>
      )}
      {!isLoading && !isError && data && (
        <PublicShareLayout sharedBy={data.shared_by}>
          {data.type === "scorecard" && (
            <PublicScorecardView payload={data.payload as SPIResult} />
          )}
          {data.type === "landscape" && (
            <PublicLandscapeView payload={data.payload as LandscapeOutputData} />
          )}
          {data.type === "rank_tracker_ai_report" && (
            <PublicRankTrackerView payload={data.payload as RankTrackerReport} />
          )}
          {data.type === "rank_tracker_page" && (
            <PublicRankTrackerPageView payload={data.payload as PublicRankTrackerPagePayload} />
          )}
          {data.type === "seo_insights" && (
            <PublicSeoInsightsView payload={data.payload as PublicSeoInsightsPayload} />
          )}
          {data.type === "search_term_set" && (
            <PublicSearchTermSetView payload={data.payload as SearchTermSet} />
          )}
        </PublicShareLayout>
      )}
    </>
  );
}
